import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  appTokenAuthContext,
  clearAppIdToken,
  getAppIdTokenFromSessionStorage,
  getAppTokenUserId,
  getThLegacyCognitoId,
  getThLegacyCognitoIdFromClaims,
  isExpiredJwt,
  isWellFormedJwt,
} from 'microsites/partner-linking/app-token';
import LinkingError from 'microsites/partner-linking/linking-error';
import LinkingLoading from 'microsites/partner-linking/linking-loading';
import PartnerLinkingAuthGate from 'microsites/partner-linking/partner-linking-auth-gate';
import { consumePkce } from 'microsites/partner-linking/pkce';
import { toSingularAttributionInput } from 'microsites/partner-linking/singular-attribution';
import { useBackToApp } from 'microsites/partner-linking/use-back-to-app';
import { useIntl } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  PartnerLinkSource,
  useCompleteLinkingMutation,
  useCompleteLinkingWithAttributionMutation,
} from 'generated/graphql-gateway';
import { useAuthContext } from 'state/auth';
import { DatadogFlag, useDatadogFlag } from 'state/datadog-flags';
import { useLocale } from 'state/intl';
import { routes } from 'utils/routing';

import {
  PartnerLinkingErrorReason,
  gatewayErrorFields,
  isCtAccountError,
  isEmailVerificationRequiredError,
  isRegionNotEligibleError,
} from '../errors';

import { CtLinkingEvent, makeCtLinkingLogger } from './log-linking';
import RegionUnavailable from './region-unavailable';
import VerificationRequired from './verification-required';

/** Terminal callback outcomes that get a specific screen, not the generic error. */
type BlockedView = 'regionUnavailable' | 'verificationRequired' | 'ctAccountError';

/**
 * OIDC callback target for the CT partnership-linking flow, mounted at
 * `/partner-linking/canadian-tire/complete-linking`.
 *
 * The redirect from CT carries `?code=<auth code>&state=<our state>`. We:
 *   1. Recover `{codeVerifier, source}` from sessionStorage by `state`
 *      (single-use; entry is removed on read). If missing, CSRF defense
 *      tripped — refuse to call the gateway.
 *   2. Call `completeLinking(partnerId, source, authCode, codeVerifier)`.
 *   3. On success, navigate to the locale-appropriate Triangle success page.
 *      On failure, render the localized error screen.
 */
function CompleteLinkingInner() {
  const [searchParams] = useSearchParams();
  const [completeLinking] = useCompleteLinkingMutation();
  const [completeLinkingWithAttribution] = useCompleteLinkingWithAttributionMutation();
  // Chooses the document. See `use-initiate-linking.ts` — the same gate has to
  // cover completion, or a guest who initiated under the flag returns to a
  // callback that sends a document the supergraph may reject.
  const forwardAttribution = useDatadogFlag(DatadogFlag.SINGULAR_ATTRIBUTION_FORWARDING);
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  // Persisted to localStorage at initiate time, so it survives the OIDC
  // round-trip and picks the right Triangle success page here.
  const { language } = useLocale();
  // Read only for the log join key below; lapsed claims describe nobody this
  // flow can act as.
  //
  // Reduced to the id here rather than handed to `run` as claims: the payload is
  // a fresh object on every read, and `run` re-fires the mutation whenever its
  // dependencies change identity. A string compares by value.
  const { currentUserSession } = useAuthContext();
  const sessionThLegacyCognitoId = currentUserSession?.isValid?.()
    ? getThLegacyCognitoIdFromClaims(currentUserSession.getIdToken().payload)
    : undefined;
  // The backend re-checks both gates here, so a guest who became ineligible
  // mid-flow gets the matching block screen rather than the generic error page.
  // The origin is only recovered from the PKCE entry inside `run`, so it is held
  // in state for the screen's CTA.
  const [blockedView, setBlockedView] = useState<BlockedView | null>(null);
  const [blockAppOrigin, setBlockAppOrigin] = useState(false);
  // Captured at block time because `showBlock` clears storage before the
  // verification screen renders.
  const [blockAppToken, setBlockAppToken] = useState<string | null>(null);
  const handleBackToApp = useBackToApp(blockAppOrigin);

  // `isAppOrigin` defaults to false (TH web home) so the error page's CTA is safe
  // on desktop, where a deep link would no-op. Callers that have recovered an
  // explicit app origin pass true.
  const goToError = useCallback(
    (reason: PartnerLinkingErrorReason, detail?: string, isAppOrigin = false) => {
      // The flow ends on the error page, so drop the handoff token.
      clearAppIdToken();
      navigate(routes.partnerLinkingError, {
        replace: true,
        state: { reason, detail, isAppOrigin },
      });
    },
    [navigate]
  );

  // Terminal with no LINKED record persisted, so the handoff token is dropped
  // exactly as the error path does.
  const showBlock = useCallback(
    (view: BlockedView, isAppOrigin: boolean, appToken: string | null) => {
      clearAppIdToken();
      setBlockAppOrigin(isAppOrigin);
      setBlockAppToken(appToken);
      setBlockedView(view);
    },
    []
  );

  const run = useCallback(async () => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const oauthError = searchParams.get('error');

    // App-origin guests have no Cognito session, so the generic logger's `userId`
    // is empty for them. Recover the Cognito `sub` from the handoff token to keep
    // failures attributable. `undefined` for web-origin, where the logger already
    // fills it in.
    const appToken = getAppIdTokenFromSessionStorage();
    const userId = getAppTokenUserId(appToken);
    // The gateway's join key, read off whichever identity the mutation
    // authenticates as: the handoff token if present, else the session.
    const thLegacyCognitoId =
      appToken !== null ? getThLegacyCognitoId(appToken) : sessionThLegacyCognitoId;
    // `source` only arrives with the PKCE entry below, so the logger is rebound
    // once it is recovered.
    let log = makeCtLinkingLogger('complete-linking', { thLegacyCognitoId, userId });

    if (oauthError) {
      log.error(CtLinkingEvent.OidcError, {
        oauthError,
        oauthErrorDescription: searchParams.get('error_description') ?? undefined,
      });
      goToError(
        PartnerLinkingErrorReason.PartnerReported,
        `${oauthError}${
          searchParams.get('error_description') ? `: ${searchParams.get('error_description')}` : ''
        }`
      );
      return;
    }

    if (!code || !state) {
      log.error(CtLinkingEvent.CallbackParamsMissing, {
        codePresent: code !== null,
        statePresent: state !== null,
      });
      goToError(
        PartnerLinkingErrorReason.MissingCodeOrState,
        'expected ?code= and ?state= on callback URL'
      );
      return;
    }

    const stored = consumePkce(state);
    if (!stored) {
      // Either CSRF (state mismatch) or the user landed here in a tab that
      // didn't kick off the flow. Either way, refuse to proceed. The returned
      // `state` is not logged: it is the storage key for the verifier.
      log.error(CtLinkingEvent.PkceSessionExpired);
      goToError(
        PartnerLinkingErrorReason.SessionExpired,
        'no pkce verifier found for returned state'
      );
      return;
    }

    // Recovered the origin: only an explicit native-app entry returns to the
    // app; everything else (incl. web) goes to the TH web home.
    const isAppOrigin = stored.source === PartnerLinkSource.THIAPP;
    log = makeCtLinkingLogger('complete-linking', {
      source: stored.source,
      thLegacyCognitoId,
      userId,
    });

    if (appToken !== null && !isWellFormedJwt(appToken)) {
      log.error(CtLinkingEvent.AppTokenMalformed);
      goToError(
        PartnerLinkingErrorReason.CallbackMalformedToken,
        'malformed app-handoff token',
        isAppOrigin
      );
      return;
    }

    // The round-trip through the partner can outlast the handoff token. Refuse
    // rather than let the mutation fall back to the browser's session, which may
    // be a different guest than the one the app handed off.
    if (appToken !== null && isExpiredJwt(appToken)) {
      log.error(CtLinkingEvent.AppTokenExpired);
      goToError(
        PartnerLinkingErrorReason.CallbackExpiredToken,
        'expired app-handoff token',
        isAppOrigin
      );
      return;
    }

    try {
      const linkingVariables = {
        partnerId: 'CT',
        source: stored.source as PartnerLinkSource,
        authCode: code,
        codeVerifier: stored.codeVerifier,
      };
      const linkingContext = appToken ? appTokenAuthContext(appToken) : undefined;
      const result = forwardAttribution
        ? await completeLinkingWithAttribution({
            variables: {
              ...linkingVariables,
              // Recovered from the PKCE blob rather than the callback URL, which
              // carries only `?code=&state=`. Sent again here (rather than copied
              // from the INITIATED row) because INITIATED rows age out at 45 days
              // while LINKED rows persist — a conversion must outlive the attempt.
              singularAttribution: toSingularAttributionInput(stored.singularAttribution),
            },
            context: linkingContext,
          })
        : await completeLinking({ variables: linkingVariables, context: linkingContext });
      if (result.data?.completeLinking?.success) {
        log.info(CtLinkingEvent.RequestCompleted);
        // The Triangle success pages are Sanity static pages rendered by the main
        // app shell, not microsite routes.
        clearAppIdToken();
        navigate(
          language === 'fr'
            ? routes.partnerLinkingCanadianTireSuccessFr
            : routes.partnerLinkingCanadianTireSuccess,
          { replace: true }
        );
        return;
      }
      log.error(CtLinkingEvent.CompleteUnsuccessful);
      goToError(
        PartnerLinkingErrorReason.CompleteUnsuccessful,
        'completeLinking returned success=false',
        isAppOrigin
      );
    } catch (err) {
      if (isRegionNotEligibleError(err)) {
        log.info(CtLinkingEvent.RegionNotEligible, { blockedBy: 'gateway' });
        showBlock('regionUnavailable', isAppOrigin, appToken);
        return;
      }
      if (isEmailVerificationRequiredError(err)) {
        // Verification lapsed between initiation and callback, or the initiate
        // gate was bypassed.
        log.info(CtLinkingEvent.EmailVerificationRequired, { blockedBy: 'gateway' });
        showBlock('verificationRequired', isAppOrigin, appToken);
        return;
      }
      // THLE-4387: CT rejected the account itself (inactive, already bound to
      // another Triangle account, loyalty-backend failure). Retrying can't fix
      // any of them, so the guest gets the "contact Triangle" screen rather
      // than the generic "go back and try again" page. Ahead of the generic
      // failure log so one outcome emits one event.
      if (isCtAccountError(err)) {
        log.error(CtLinkingEvent.CtAccountError, gatewayErrorFields(err));
        showBlock('ctAccountError', isAppOrigin, appToken);
        return;
      }
      log.error(CtLinkingEvent.CompleteFailed, gatewayErrorFields(err));
      goToError(
        PartnerLinkingErrorReason.CompleteError,
        err instanceof Error ? err.message : String(err),
        isAppOrigin
      );
    }
  }, [
    searchParams,
    completeLinking,
    completeLinkingWithAttribution,
    forwardAttribution,
    navigate,
    goToError,
    showBlock,
    language,
    sessionThLegacyCognitoId,
  ]);

  // `consumePkce` is single-use, so this callback has exactly one chance to
  // succeed: a second pass finds nothing in sessionStorage and cannot tell
  // that from a replayed or forged `state`, so it reports SESSION_EXPIRED over
  // a link the first pass already completed — the guest sees a failure, and
  // the funnel records a CSRF event that never happened.
  //
  // That makes running once an invariant of the page rather than a property of
  // whichever values `run` happens to close over. The flag is the live example:
  // `provider.ts` moves it under a mounted page in two ways — the 3s init
  // timeout renders on fallbacks and corrects when the configuration lands, and
  // `setDatadogFlagsIdentity` re-evaluates once the session settles — and every
  // other dependency being identity-stable is what kept this latent until now.
  // `initiate-linking.tsx` latches the same way for the same reason.
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (hasRunRef.current) {
      return;
    }
    hasRunRef.current = true;
    void run();
  }, [run]);

  if (blockedView === 'regionUnavailable') {
    return <RegionUnavailable onBackToApp={handleBackToApp} />;
  }

  if (blockedView === 'verificationRequired') {
    return <VerificationRequired onBackToApp={handleBackToApp} idToken={blockAppToken} />;
  }

  if (blockedView === 'ctAccountError') {
    return (
      <LinkingError
        title={formatMessage({ id: 'ctLinkingAccountErrorTitle' })}
        message={formatMessage({ id: 'ctLinkingAccountErrorDescription' })}
        onBack={handleBackToApp}
      />
    );
  }

  return <LinkingLoading />;
}

export default function CanadianTireCompleteLinking() {
  return (
    <PartnerLinkingAuthGate>
      <CompleteLinkingInner />
    </PartnerLinkingAuthGate>
  );
}
